import React, { useState, useRef, useEffect } from 'react';
import '../styles/create-exam-modal.css';
import API from '../api/api';
import { clearDraft, loadDraft, saveDraft } from '../utils/draftStorage';
const EMPTY_EXAM_FORM = {
  examName: '',
  description: '',
  scoringMethod: 'Percentage',
  passingScore: 0,
  sections: [],
  optionalSections: [],
  minOptional: 1,
  maxOptional: 3,
};
const getExamDraftKey = (id) => id ? `exam-management:edit:${id}` : 'exam-management:create';

const parseWholeNumber = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const sumDifficultyCounts = (section) => (
  parseWholeNumber(section.easy) +
  parseWholeNumber(section.medium) +
  parseWholeNumber(section.difficult)
);

const redistributeDifficultyCounts = (section, nextTotal) => {
  const current = [
    parseWholeNumber(section.easy),
    parseWholeNumber(section.medium),
    parseWholeNumber(section.difficult),
  ];
  const currentTotal = current.reduce((sum, value) => sum + value, 0);

  if (nextTotal <= 0) {
    return { easy: '0', medium: '0', difficult: '0' };
  }

  if (currentTotal <= 0) {
    const base = Math.floor(nextTotal / 3);
    const remainder = nextTotal - base * 3;
    return {
      easy: String(base + (remainder > 0 ? 1 : 0)),
      medium: String(base + (remainder > 1 ? 1 : 0)),
      difficult: String(base),
    };
  }

  const scaled = current.map((value) => Math.floor((value / currentTotal) * nextTotal));
  let remainder = nextTotal - scaled.reduce((sum, value) => sum + value, 0);

  const fractions = current
    .map((value, index) => ({
      index,
      fraction: (value / currentTotal) * nextTotal - scaled[index],
    }))
    .sort((a, b) => b.fraction - a.fraction);

  for (let i = 0; i < fractions.length && remainder > 0; i += 1) {
    scaled[fractions[i].index] += 1;
    remainder -= 1;
  }

  return {
    easy: String(scaled[0]),
    medium: String(scaled[1]),
    difficult: String(scaled[2]),
  };
};

const isSectionConfigValid = (section) => {
  const numQuestions = parseWholeNumber(section.numQuestions);
  const timeMinutes = parseWholeNumber(section.timeMinutes);
  const easy = parseWholeNumber(section.easy);
  const medium = parseWholeNumber(section.medium);
  const difficult = parseWholeNumber(section.difficult);

  return (
    numQuestions > 0 &&
    timeMinutes > 0 &&
    easy >= 0 &&
    medium >= 0 &&
    difficult >= 0 &&
    numQuestions === easy + medium + difficult
  );
};

const isAlwaysCompulsorySection = (section = {}) => (
  ['aptitude', 'logical reasoning'].includes(String(section.name || '').trim().toLowerCase())
);

// AVAILABLE_OPTIONAL_SECTIONS removed in favor of dynamic fetching

const IconClose = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M1 1L12 12M12 1L1 12" stroke="#94A3B8" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const IconCaret = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2 4.5L7 9.5L12 4.5" stroke="#26282B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconTrash = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

// ────────────────────────────────────────
//  STEP 1 — Basic Information
// ────────────────────────────────────────
const StepBasicInfo = ({ data, onChange }) => {
  return (
    <div className="cem-step-content cem-basic-inline">
      <div className="cem-field">
        <label className="cem-label">Exam Name<span className="required-star">*</span></label>
        <input
          className="cem-input"
          placeholder="e.g. Python Developer Assessment"
          value={data.examName}
          onChange={e => onChange('examName', e.target.value)}
        />
      </div>

      <div className="cem-field">
        <label className="cem-label">Description</label>
        <input
          className="cem-input"
          placeholder="Brief description of this exam..."
          value={data.description}
          onChange={e => onChange('description', e.target.value)}
        />
      </div>

    </div>
  );
};

// ────────────────────────────────────────
//  SECTION BLOCK
// ────────────────────────────────────────
const SectionBlock = ({ section, isOptional, onUpdate, onRemove }) => {
  const questionEditBaselineRef = useRef(null);
  const lockedCompulsory = !isOptional || isAlwaysCompulsorySection(section);

  const update = (field, value) => {
    const numericValue = value.replace(/[^\d]/g, '');

    if (field === 'numQuestions') {
      const nextTotal = parseWholeNumber(numericValue);
      const baselineSection = questionEditBaselineRef.current || section;
      const redistributed = redistributeDifficultyCounts(baselineSection, nextTotal);

      onUpdate({
        ...section,
        numQuestions: numericValue,
        ...redistributed,
      });
      return;
    }

    if (field === 'easy' || field === 'medium' || field === 'difficult') {
      const nextSection = { ...section, [field]: numericValue };
      onUpdate({
        ...nextSection,
        numQuestions: String(sumDifficultyCounts(nextSection)),
      });
      return;
    }

    onUpdate({ ...section, [field]: numericValue });
  };

  return (
    <div className="cem-section-block">
      <div className="cem-section-details-row">
        <div className="cem-section-name-cell">
          <span className="cem-section-name">{section.name}</span>
        </div>

        <div className="cem-field cem-field--half">
          <input
            className="cem-input"
            type="text"
            inputMode="numeric"
            value={section.numQuestions}
            onFocus={() => {
              questionEditBaselineRef.current = {
                ...section,
                easy: String(parseWholeNumber(section.easy)),
                medium: String(parseWholeNumber(section.medium)),
                difficult: String(parseWholeNumber(section.difficult)),
              };
            }}
            onBlur={() => {
              questionEditBaselineRef.current = null;
            }}
            onChange={e => update('numQuestions', e.target.value)}
          />
        </div>

        <div className="cem-field cem-field--half">
          <input
            className="cem-input"
            type="text"
            inputMode="numeric"
            value={section.timeMinutes}
            onChange={e => update('timeMinutes', e.target.value)}
          />
        </div>

        <div className="cem-field cem-field--difficulty">
          <div className="cem-difficulty-row">
            <div className="cem-difficulty-item">
              <span className="cem-difficulty-label">Easy</span>
              <input
                className="cem-input cem-input--sm"
                type="text"
                inputMode="numeric"
                value={section.easy}
                onChange={e => update('easy', e.target.value)}
              />
            </div>
            <div className="cem-difficulty-item">
              <span className="cem-difficulty-label">Medium</span>
              <input
                className="cem-input cem-input--sm"
                type="text"
                inputMode="numeric"
                value={section.medium}
                onChange={e => update('medium', e.target.value)}
              />
            </div>
            <div className="cem-difficulty-item">
              <span className="cem-difficulty-label">Hard</span>
              <input
                className="cem-input cem-input--sm"
                type="text"
                inputMode="numeric"
                value={section.difficult}
                onChange={e => update('difficult', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="cem-compulsory-cell">
          <label className="cem-compulsory-toggle">
            <input
              type="checkbox"
              checked={lockedCompulsory || Boolean(section.isCompulsory)}
              disabled={lockedCompulsory}
              onChange={e => onUpdate({ ...section, isCompulsory: e.target.checked })}
            />
          </label>
        </div>

        <div className="cem-action-cell">
          {isOptional && (
            <button className="cem-section-delete" onClick={onRemove} aria-label={`Delete ${section.name}`}>
              <IconTrash />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────
//  STEP 2 — Sections
// ────────────────────────────────────────
const StepSections = ({ data, onChange, availableOptionalSections }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pendingSelections, setPendingSelections] = useState([]);
  const dropdownRef = useRef(null);

  const addedNames = data.optionalSections.map(s => s.name);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setPendingSelections([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const togglePending = (name) => {
    setPendingSelections(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const handleAddSelected = () => {
    const newSections = pendingSelections
      .filter(name => !addedNames.includes(name))
      .map(name => ({
        name,
        numQuestions: 10,
        timeMinutes: 15,
        medium: 4,
        easy: 3,
        difficult: 3,
        isCompulsory: false
      }));
    onChange('optionalSections', [...newSections, ...data.optionalSections]);
    setPendingSelections([]);
    setDropdownOpen(false);
  };

  const updateCompulsory = (idx, updated) => {
    const sections = [...data.sections];
    sections[idx] = updated;
    onChange('sections', sections);
  };

  const updateOptional = (idx, updated) => {
    const sections = [...data.optionalSections];
    sections[idx] = updated;
    onChange('optionalSections', sections);
  };

  const removeOptional = (idx) => {
    onChange('optionalSections', data.optionalSections.filter((_, i) => i !== idx));
  };

  return (
    <div className="cem-step-content cem-step-content--sections">
      <div className="cem-sections-topbar">
        <h3 className="cem-sections-title">Sections</h3>
        <div className="cem-add-optional-wrap" ref={dropdownRef}>
          <button
            className="cem-add-optional-btn"
            onClick={() => {
              setPendingSelections([]);
              setDropdownOpen(prev => !prev);
            }}
          >
            <span className="cem-add-icon">+</span>
            Add Optional Section
            <span className="cem-add-caret"><IconCaret /></span>
          </button>

          {dropdownOpen && (
            <div className="cem-optional-dropdown">
              <div className="cem-optional-dropdown-header">
                <span className="cem-optional-dropdown-title">Add Optional Section</span>
                <button
                  className="cem-optional-dropdown-close"
                  onClick={() => { setPendingSelections([]); setDropdownOpen(false); }}
                >
                  <IconClose />
                </button>
              </div>

              <div className="cem-optional-dropdown-subheader">
                <span className="cem-optional-avail-label">Available Sections</span>
              </div>

              <div className="cem-optional-dropdown-list">
                {availableOptionalSections.map(name => {
                  const alreadyAdded = addedNames.includes(name);
                  const isChecked = pendingSelections.includes(name) || alreadyAdded;

                  return (
                    <label
                      key={name}
                      className={`cem-optional-dropdown-item ${alreadyAdded ? 'cem-optional-dropdown-item--disabled' : ''}`}
                    >
                      <span className={`cem-custom-checkbox ${isChecked ? 'cem-custom-checkbox--checked' : ''}`} />
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={alreadyAdded}
                        onChange={() => !alreadyAdded && togglePending(name)}
                        style={{ display: 'none' }}
                      />
                      <span className="cem-optional-name">{name}</span>
                      {alreadyAdded && <span className="cem-optional-added-tag">Added</span>}
                    </label>
                  );
                })}
              </div>

              <div className="cem-optional-dropdown-footer">
                <button
                  className="cem-optional-cancel-btn"
                  onClick={() => { setPendingSelections([]); setDropdownOpen(false); }}
                >
                  Cancel
                </button>
                <button
                  className="cem-optional-confirm-btn"
                  disabled={pendingSelections.length === 0}
                  onClick={handleAddSelected}
                >
                  Add ({pendingSelections.length})
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="cem-sections-scroll">
        <div className="cem-section-table-header">
          <span>Section</span>
          <span>Number Of Questions</span>
          <span>Time</span>
          <span>Difficulty Breakdown</span>
          <span>Req.</span>
          <span></span>
        </div>

        {data.optionalSections.length > 0 && (
          <>
            {/* <h3 className="cem-optional-heading">Optional Sections</h3> */}
            {data.optionalSections.map((section, idx) => (
              <SectionBlock
                key={idx}
                section={section}
                isOptional={true}
                onUpdate={(updated) => updateOptional(idx, updated)}
                onRemove={() => removeOptional(idx)}
              />
            ))}
          </>
        )}

        {data.sections.map((section, idx) => (
            <SectionBlock
              key={idx}
              section={section}
              isOptional={false}
              onUpdate={(updated) => updateCompulsory(idx, updated)}
              onRemove={null}
            />
        ))}
      </div>

    </div>
  );
};

// ────────────────────────────────────────
//  MAIN MODAL
// ────────────────────────────────────────
const CreateExamModal = ({ onClose, onSuccess, examToEdit, initialExamData = null, modalTitle }) => {
  const draftKey = initialExamData?.draftKey || getExamDraftKey(examToEdit?.id);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState(() => {
    const sourceExam = examToEdit || initialExamData;
    const defaultData = sourceExam ? {
      examName: sourceExam.title || '',
      description: sourceExam.description || '',
      scoringMethod: sourceExam.scoring_method || 'Percentage',
      passingScore: 0,
      sections: (sourceExam.sections_config || [])
        .filter(s => Boolean(s.isCompulsory ?? s.is_compulsory ?? !Boolean(s.isOptional ?? s.is_optional)))
        .map((s) => ({
        name: s.name,
        numQuestions: s.numQuestions ?? 10,
        timeMinutes: s.timeMinutes ?? 15,
        medium: s.medium ?? 4,
        easy: s.easy ?? 3,
        difficult: s.difficult ?? 3,
        isCompulsory: Boolean(s.isCompulsory || s.is_compulsory),
      })),
      optionalSections: (sourceExam.sections_config || [])
        .filter(s => Boolean(s.isOptional ?? s.is_optional) && !Boolean(s.isCompulsory ?? s.is_compulsory))
        .map((s) => ({
        name: s.name,
        numQuestions: s.numQuestions ?? 10,
        timeMinutes: s.timeMinutes ?? 15,
        medium: s.medium ?? 4,
        easy: s.easy ?? 3,
        difficult: s.difficult ?? 3,
      })),
      minOptional: sourceExam.min_optional ?? 1,
      maxOptional: sourceExam.max_optional ?? 3,
    } : EMPTY_EXAM_FORM;

    return loadDraft(draftKey, defaultData);
  });
  
  const [availableOptionalSections, setAvailableOptionalSections] = useState([]);

  const basicInfoValid = formData.examName.trim().length > 0;
  const selectableOptionalCount = (formData.optionalSections || []).filter(section => !section.isCompulsory).length;
  const sectionsValid =
    formData.sections.length > 0 &&
    formData.sections.every(isSectionConfigValid) &&
    formData.optionalSections.every(isSectionConfigValid);
  const canSubmit = basicInfoValid && sectionsValid;

  useEffect(() => {
    saveDraft(draftKey, formData);
  }, [draftKey, formData]);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const { data } = await API.get('/sections');
        const optional = data.sections.filter(s => !s.is_compulsory).map(s => s.name);
        setAvailableOptionalSections(optional);

        // Only set default compulsory sections if NOT editing
        if (!examToEdit && !initialExamData) {
          const compulsory = data.sections
            .filter(s => s.is_compulsory)
            .map(s => ({
              name: s.name,
              numQuestions: 10,
              timeMinutes: 15,
              medium: 4,
              easy: 3,
              difficult: 3
            }));
          
          setFormData(prev => ({
            ...prev,
            sections: prev.sections.length > 0 ? prev.sections : compulsory
          }));
        }
      } catch (err) {
        console.error('Failed to fetch sections:', err);
        setError('Failed to load sections from server.');
      }
    };
    fetchSections();
  }, [examToEdit, initialExamData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.examName.trim()) {
      setError('Exam name is required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...formData,
        sections: formData.sections.map(section => ({ ...section, isOptional: false, isCompulsory: true })),
        optionalSections: formData.optionalSections.map(section => ({
          ...section,
          isOptional: !(section.isCompulsory || isAlwaysCompulsorySection(section)),
          isCompulsory: Boolean(section.isCompulsory || isAlwaysCompulsorySection(section)),
        })),
        scoringMethod: 'Percentage',
        passingScore: 0,
        minOptional: selectableOptionalCount > 0 ? 1 : 0,
        maxOptional: selectableOptionalCount,
      };
      if (examToEdit) {
        await API.put(`/exam/${examToEdit.id}`, payload);
      } else {
        await API.post('/exam', payload);
      }
      clearDraft(draftKey);
      if (onSuccess) onSuccess({ mode: examToEdit ? 'edit' : 'create' });
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || 'Failed to save exam. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClose = () => {
    if (onClose) {
      onClose({
        reason: 'overlay',
        draftSaved: true,
      });
    }
  };

  const handleCloseButton = () => {
    if (onClose) {
      onClose({
        reason: 'close-button',
        draftSaved: false,
      });
    }
  };

  return (
    <div className="cem-overlay" onClick={handleOverlayClose}>
      <div className="cem-modal cem-modal--wide" onClick={e => e.stopPropagation()}>

        <div className="cem-header">
          <div>
            <h2 className="cem-title">{modalTitle || (examToEdit ? 'Edit Exam' : 'Create Exam')}</h2>
          </div>
          <button className="cem-close-btn" onClick={handleCloseButton}>
            <IconClose />
          </button>
        </div>

        <div className="cem-panel">
          <StepBasicInfo data={formData} onChange={handleChange} />
          <StepSections data={formData} onChange={handleChange} availableOptionalSections={availableOptionalSections} />
        </div>

        {error && (
          <div className="cem-error-bar">{error}</div>
        )}

        <div className="cem-footer">
          <button className="cem-btn-next" onClick={handleSubmit} disabled={loading || !canSubmit}>
            {loading ? 'Saving...' : (examToEdit ? 'Save Changes' : 'Submit')}
          </button>
        </div>

      </div>
    </div>
  );
};

export default CreateExamModal;
